import Header from "@/components/Header";
import Footer from "@/components/Footer";
import SEO, { organizationSchema, createFAQSchema, softwareApplicationSchema } from "@/components/SEO";
import { Button } from "@/components/ui/button";
import { Check, Star, Palette, Zap, Layers, Shield, Download, RefreshCw } from "lucide-react";
import { Link } from "react-router-dom";

const AILogoGenerator = () => {
  const faqs = [
    {
      question: "How does the AI logo generator work?",
      answer: "Simply describe your brand, industry, and style preferences. Our AI uses advanced FLUX models to generate unique logo concepts. You can specify colors, styles (minimalist, vintage, modern), and symbols to include."
    },
    {
      question: "Can I use AI-generated logos commercially?",
      answer: "Yes! All logos generated on paid plans come with full commercial rights. Use them for your business, products, marketing materials - anywhere you need."
    },
    {
      question: "How many logo variations can I generate?",
      answer: "Free users get 5 generations daily. Pro plans offer 500+ generations monthly, perfect for exploring multiple concepts or creating logos for multiple brands."
    },
    {
      question: "What styles of logos can I create?",
      answer: "Our AI supports all major logo styles: wordmarks, lettermarks, emblems, mascots, abstract marks, and combination marks. Just describe your vision in the prompt."
    },
    {
      question: "Are the logos unique to my brand?",
      answer: "Yes, every logo is uniquely generated based on your specific prompt. No templates, no recycled designs. Each creation is original."
    }
  ];

  const features = [
    {
      icon: <Palette className="w-6 h-6" />,
      title: "Endless Styles",
      description: "Minimalist, vintage, modern, playful - generate any style imaginable."
    },
    {
      icon: <Zap className="w-6 h-6" />,
      title: "Instant Generation",
      description: "Get professional logo concepts in seconds, not days or weeks."
    },
    {
      icon: <RefreshCw className="w-6 h-6" />,
      title: "Unlimited Iterations",
      description: "Not quite right? Generate variations until you find the perfect logo."
    },
    {
      icon: <Shield className="w-6 h-6" />,
      title: "Full Ownership",
      description: "Commercial rights included. Your logo, your brand, fully yours."
    }
  ];

  const industries = [
    "Tech Startups",
    "Restaurants & Food",
    "Fashion & Beauty",
    "Healthcare",
    "Real Estate",
    "E-commerce",
    "Consulting",
    "Creative Agencies"
  ];

  return (
    <div className="min-h-screen w-full overflow-x-hidden">
      <SEO
        title="AI Logo Generator | Create Unique Brand Logos in Seconds"
        description="Generate professional AI logos for your brand. Unique designs, instant delivery, full commercial rights. No design skills needed. Try free - create your perfect logo now!"
        keywords="ai logo generator, logo maker ai, brand logo creator, ai logo design, business logo generator, free logo maker ai, professional logo ai, startup logo generator"
        url="/ai-logo-generator"
        jsonLd={[
          organizationSchema,
          softwareApplicationSchema,
          createFAQSchema(faqs)
        ]}
        breadcrumbs={[
          { name: 'Home', url: '/' },
          { name: 'AI Logo Generator', url: '/ai-logo-generator' }
        ]}
      />
      
      <Header />
      
      <main className="w-full">
        {/* Hero Section */}
        <section className="relative py-20 lg:py-32 bg-gradient-to-br from-background via-background to-primary/5">
          <div className="container mx-auto px-4">
            <div className="max-w-4xl mx-auto text-center">
              <div className="inline-flex items-center gap-2 bg-primary/10 text-primary px-4 py-2 rounded-full text-sm font-medium mb-6">
                <Star className="w-4 h-4 fill-current" />
                60,000+ Logos Created Monthly
              </div>
              
              <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold mb-6">
                AI Logo Generator
                <span className="block text-primary mt-2">Unique Brands in Seconds</span>
              </h1>
              
              <p className="text-xl text-muted-foreground mb-8 max-w-2xl mx-auto">
                Create stunning, unique logos with AI. No design skills required. 
                Professional quality, instant delivery, full commercial rights.
              </p>
              
              <div className="flex flex-col sm:flex-row gap-4 justify-center mb-8">
                <Button asChild size="lg" className="text-lg px-8">
                  <Link to="/try-generator">Create Free Logo</Link>
                </Button>
                <Button asChild variant="outline" size="lg" className="text-lg px-8">
                  <Link to="/gallery">View Examples</Link>
                </Button>
              </div>
              
              <div className="flex items-center justify-center gap-6 text-sm text-muted-foreground">
                <span className="flex items-center gap-1">
                  <Check className="w-4 h-4 text-green-500" /> No signup required
                </span>
                <span className="flex items-center gap-1">
                  <Check className="w-4 h-4 text-green-500" /> Unique designs
                </span>
                <span className="flex items-center gap-1">
                  <Check className="w-4 h-4 text-green-500" /> Commercial rights
                </span>
              </div>
            </div>
          </div>
        </section>

        {/* Features Grid */}
        <section className="py-20 bg-muted/30">
          <div className="container mx-auto px-4">
            <div className="text-center mb-12">
              <h2 className="text-3xl font-bold mb-4">Why Brands Choose AI Logo Generation</h2>
              <p className="text-muted-foreground max-w-2xl mx-auto">
                Professional logos without the professional price tag or timeline.
              </p>
            </div>
            
            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6 max-w-6xl mx-auto">
              {features.map((feature, index) => (
                <div key={index} className="bg-card p-6 rounded-xl border shadow-sm hover:shadow-md transition-shadow">
                  <div className="w-12 h-12 bg-primary/10 rounded-lg flex items-center justify-center text-primary mb-4">
                    {feature.icon}
                  </div>
                  <h3 className="font-semibold text-lg mb-2">{feature.title}</h3>
                  <p className="text-muted-foreground text-sm">{feature.description}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Industries */}
        <section className="py-20">
          <div className="container mx-auto px-4">
            <div className="max-w-4xl mx-auto">
              <div className="text-center mb-12">
                <h2 className="text-3xl font-bold mb-4">Logos for Every Industry</h2>
                <p className="text-muted-foreground">
                  AI logos trusted by businesses across all sectors
                </p>
              </div>
              
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                {industries.map((industry, index) => (
                  <div key={index} className="flex items-center gap-2 bg-muted/50 p-4 rounded-lg">
                    <Layers className="w-5 h-5 text-primary flex-shrink-0" />
                    <span className="text-sm font-medium">{industry}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* Stats Section */}
        <section className="py-16 bg-primary text-primary-foreground">
          <div className="container mx-auto px-4">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-8 max-w-4xl mx-auto text-center">
              <div>
                <div className="text-4xl font-bold mb-2">60K+</div>
                <div className="text-primary-foreground/80">Logos Created</div>
              </div>
              <div>
                <div className="text-4xl font-bold mb-2">&lt;15s</div>
                <div className="text-primary-foreground/80">Generation Time</div>
              </div>
              <div>
                <div className="text-4xl font-bold mb-2">100%</div>
                <div className="text-primary-foreground/80">Unique Designs</div>
              </div>
              <div>
                <div className="text-4xl font-bold mb-2">$0</div>
                <div className="text-primary-foreground/80">To Start</div>
              </div>
            </div>
          </div>
        </section>

        {/* FAQ Section */}
        <section className="py-20">
          <div className="container mx-auto px-4">
            <div className="max-w-3xl mx-auto">
              <h2 className="text-3xl font-bold text-center mb-12">Frequently Asked Questions</h2>
              
              <div className="space-y-6">
                {faqs.map((faq, index) => (
                  <div key={index} className="bg-card border rounded-lg p-6">
                    <h3 className="font-semibold text-lg mb-2">{faq.question}</h3>
                    <p className="text-muted-foreground">{faq.answer}</p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="py-20 bg-muted/30">
          <div className="container mx-auto px-4 text-center">
            <h2 className="text-3xl font-bold mb-4">Ready to Create Your Brand Logo?</h2>
            <p className="text-muted-foreground mb-8 max-w-xl mx-auto">
              Join thousands of entrepreneurs using AI to build memorable brands. Start free, upgrade when ready.
            </p>
            <Button asChild size="lg" className="text-lg px-8">
              <Link to="/try-generator">Create Your Logo Free</Link>
            </Button>
          </div>
        </section>
      </main>
      
      <Footer />
    </div>
  );
};

export default AILogoGenerator;
